import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import type { UseFormSetValue } from 'react-hook-form';
import type {
  TGuaranteesAccountOption,
  TGuaranteesContractOption,
  TGuaranteesRootSectionFormValues,
} from '../../rootSection/types.ts';
import {
  areGuaranteesContractIdentitiesEqual,
  areGuaranteesContractNumbersEqual,
} from '../../rootSection/contractIdentity.ts';
import { findGuaranteesRootSectionCurrencyOption } from '../../rootSection/utils.ts';

export const isGuaranteesAccountOption = (
  option?: TAutocompleteOption | null
): option is TGuaranteesAccountOption =>
  !!option && 'iban' in option && 'targetField' in option;

export const isGuaranteesContractOption = (
  option?: TAutocompleteOption | null
): option is TGuaranteesContractOption =>
  !!option && 'mainDealNumber' in option && 'mainDealDate' in option;

export const areSameContractOptions = (
  option: TGuaranteesContractOption,
  selectedOption: TGuaranteesContractOption
): boolean => areGuaranteesContractIdentitiesEqual(option, selectedOption);

export const findMatchingContractOption = (
  options: TGuaranteesContractOption[],
  selectedOption: TGuaranteesContractOption
): TGuaranteesContractOption | null =>
  options.find((option) => areSameContractOptions(option, selectedOption)) ??
  options.find((option) => areGuaranteesContractNumbersEqual(option, selectedOption)) ??
  null;

export const areSameAccountOptions = (
  option: TGuaranteesAccountOption,
  selectedOption: TGuaranteesAccountOption
): boolean =>
  option.targetField === selectedOption.targetField &&
  option.iban.trim().toUpperCase() === selectedOption.iban.trim().toUpperCase();

export const mergeSelectedAccountOption = (
  options: TGuaranteesAccountOption[],
  selectedOption?: TAutocompleteOption | null
): TGuaranteesAccountOption[] => {
  if (!isGuaranteesAccountOption(selectedOption)) {
    return options;
  }

  if (options.some((option) => areSameAccountOptions(option, selectedOption))) {
    return options;
  }

  return [selectedOption, ...options];
};

export const clearGuaranteesContractSelection = (
  setValue: UseFormSetValue<TGuaranteesRootSectionFormValues>
) => {
  setValue('contract', null, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealId', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealNumber', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDate', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDateFrom', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDateTo', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealCurrency', null, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealAmount', '', { shouldDirty: true, shouldTouch: true });
  setValue('mainDealLimitBalance', '', { shouldDirty: true, shouldTouch: true });
};

export const applyGuaranteesContractOptionToForm = (
  option: TGuaranteesContractOption,
  setValue: UseFormSetValue<TGuaranteesRootSectionFormValues>
) => {
  setValue('contract', option, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealId', option.mainDealId, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealNumber', option.mainDealNumber, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDate', option.mainDealDate, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDateFrom', option.mainDealDateFrom, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealDateTo', option.mainDealDateTo, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealCurrency', findGuaranteesRootSectionCurrencyOption(option.mainDealCurrency), {
    shouldDirty: true,
    shouldTouch: true,
  });
  setValue('mainDealAmount', option.mainDealAmount, { shouldDirty: true, shouldTouch: true });
  setValue('mainDealLimitBalance', option.mainDealLimitBalance, {
    shouldDirty: true,
    shouldTouch: true,
  });
};
